import { Canvg } from 'canvg'
import { jsPDF } from "jspdf"
import slugify from "@sindresorhus/slugify"

import fontBold from '../../../collectionbrowse-components/fonts/Montserrat/Montserrat-Bold.ttf'
import fontRegular from '../../../collectionbrowse-components/fonts/Montserrat/Montserrat-Regular.ttf'

import objectImage from '../../../images/new-icons/Icon_240_art.svg'
import archiveImage from '../../../images/new-icons/Icon_240_archives.svg'
import libraryImage from '../../../images/new-icons/Icon_240_books.svg'
import exhibitionImage from '../../../images/new-icons/Icon_240_exhibition.svg'
import actorImage from '../../../images/new-icons/Icon_240_peopleorgs.svg'
import personalImage from '../../../images/new-icons/Icon_240_belongings.svg'
import materialsImage from '../../../images/new-icons/Icon_240_materials.svg'

const SECTION_HEADERS = {
  'title-1999-catalogue-raisonne': 'Title (1999 Catalogue Raisonné)',
  'alternate-title-1999-catalogue-raisonne': 'Alternate Title (1999 Catalogue Raisonné)',
  'alternate-title': 'Alternate Title',
  'selected-exhibition-titles': 'Selected Exhibition Titles',
  materials: 'Materials',
  technique: 'Technique',
}

/**
 * @class PDFPrinter
 * 
 * Functions for generating and downloading a PDF from data
 **/ 
class PDFPrinter {
  topMargin = 72 // 1 in.
  bottomMargin = 54 // 3/4 in.
  leftMargin = 36 // 1/2 in.
  rightMargin = 36 // 1/2 in.

  footerTextSize = 8
  footerBottomMargin = 27 // 3/8 in.

  headingSize = 20
  subheadingSize = 12

  titleSize = 11
  bodySize = 11
  bodyTextMargin = 10

  imageWidth = 128
  imageRightMargin = 20
  imageLeftMargin = 20
  imageBottomMargin = 10

  separatorLength = 420
  separatorMargin = 28

  sectionHeadingSize = 12
  sectionInsetMargin = 5

  tombstoneBottomMargin = 10

  async blobB64(blob) {
    return await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(blob)
      reader.onload = () => {
        resolve(reader.result)
      }
      reader.onerror = (erorr) => {
        reject(error)
      }
    })
  }
  // Convert the PDF to a blob URL and download it
  download(pdf, name) {
    const fileBlob = pdf.output('bloburl')
    downloadBlobUrl(fileBlob, `${ slugifyList(name) }.pdf`)
  }

  // Fetch PDF fonts and convert to B64, store them on the class
  async fetchFonts(pdf) {
    const response = await fetch('/assets/fonts/Montserrat-Regular.ttf')
    const data = await response.blob()
    const b64 = await this.blobB64(data)
    this.regularFont = b64.replace('data:font/ttf;base64,', '')

    const boldResponse = await fetch('/assets/fonts/Montserrat-Bold.ttf')
    const boldData = await boldResponse.blob()
    const boldB64 = await this.blobB64(boldData)
    this.boldFont = boldB64.replace('data:font/ttf;base64,', '')
  }

  async setupFonts(pdf) {
    await this.fetchFonts(pdf)
    pdf.addFileToVFS('Montserrat-Regular.ttf', this.regularFont)
    pdf.addFileToVFS('Montserrat-Bold.ttf', this.boldFont)

    pdf.addFont('Montserrat-Regular.ttf', 'Montserrat', 'normal')
    pdf.addFont('Montserrat-Bold.ttf', 'Montserrat', 'bold')
  }

  addPage(pdf) {
    pdf.addPage()

    this.addFooter(pdf)
    this.cursor = { x: this.leftMargin, y: this.topMargin }
  }

  addFooter(pdf) {
    const formatter = new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    })

    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()

    const footerY = pageHeight - this.footerBottomMargin

    // Add the page number
    const pageNumber = pdf.internal.getNumberOfPages()
    const pageLabel = `Page ${pageNumber}`
    const labelWidth = pdf.getTextWidth(pageLabel)

    const pageX = pageWidth - this.rightMargin - labelWidth

    pdf.setFont('Montserrat', 'normal')
    pdf.setFontSize(this.footerTextSize)
    pdf.text(pageLabel, pageX, footerY)

    // Add title, date, time of printing
    const truncated = this.documentName.slice(0,126)
    const label = `Exported from Access O'Keeffe - ${truncated}`
    const dateline = `${window.location.href} on ${formatter.format(new Date())}`

    pdf.text(label, this.leftMargin, footerY)
    pdf.text(dateline, this.leftMargin, footerY + this.footerTextSize * pdf.getLineHeightFactor())
  }

  addListHeader(pdf, name, description, created, updated) {
    const lineFactor = pdf.getLineHeightFactor()
    const innerWidth = pdf.internal.pageSize.getWidth() - this.leftMargin - this.rightMargin

    pdf.setFontSize(this.headingSize)
    pdf.setFont('Montserrat', 'normal')

    const nameLines = pdf.splitTextToSize(name, innerWidth)
    pdf.text(nameLines, this.cursor.x, this.cursor.y)
    this.cursor = { ...this.cursor, y: this.cursor.y + nameLines.length * this.headingSize * lineFactor }

    pdf.setFontSize(this.subheadingSize)

    if (description) {
      const descriptionLines = pdf.splitTextToSize(description, innerWidth) 
      pdf.text(descriptionLines, this.cursor.x, this.cursor.y)    
      this.cursor = { ...this.cursor, y: this.cursor.y + descriptionLines.length * this.subheadingSize * lineFactor + this.tombstoneBottomMargin }      
    }

    // Add created, updated
    pdf.setFontSize(this.subheadingSize)
    const createdLabel = `Created ${created}`
    const createdWidth = pdf.getTextWidth(createdLabel)
    pdf.text(createdLabel, this.cursor.x, this.cursor.y)

    if (updated) {
      // TODO: insert a vertical line
      const label = `Updated ${updated}`
      const labelWidth = pdf.getTextWidth(label)
      const pageWidth = pdf.internal.pageSize.getWidth()

      pdf.text(label, pageWidth - this.rightMargin - labelWidth, this.cursor.y)
    }
    this.cursor = { ...this.cursor, y: this.cursor.y + this.bodySize * lineFactor }

    this.addSeparatorLine(pdf)
  }

  addSeparatorLine(pdf) {
    const pageWidth = pdf.internal.pageSize.getWidth()
    const length = pageWidth - this.leftMargin - this.rightMargin

    pdf.setDrawColor('#E0D9CB')
    pdf.setLineWidth(1)
    pdf.line( this.leftMargin, this.cursor.y, this.leftMargin + length, this.cursor.y, 'S')
    this.cursor = { ...this.cursor, y: this.cursor.y + this.separatorMargin }
  }

  async addText(pdf, text, size, style) {
    pdf.setFontSize(size)
    pdf.setFont('Montserrat', style)
    pdf.text(text, this.cursor.x, this.cursor.y, { baseline: 'alphabetic' })

    const lineCount = Array.isArray(text) ? text.length : 1

    this.cursor = { ...this.cursor, y: this.cursor.y + lineCount * size * pdf.getLineHeightFactor() }
  }

  async addImage(pdf, url, caption) {
    // Insert this item's image -- appending '/' to get correct URL math
    const jpegUrl = new URL(`full/${this.imageWidth},/0/default.jpg`, url + '/')
    const image = await fetch(jpegUrl.href, { headers: { "Accept": "image/jpeg" } })
    const blob = await image.blob()
    const bmp = await createImageBitmap(blob)

    const imageB64 = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(blob)
      reader.onload = () => {
        resolve(reader.result)
      }
    }) 

    // If image overflows the page, break the page
    const { height, width } = bmp
    const heightPt = height * 0.75
    const widthPt = this.imageWidth * 0.75

    if (this.cursor.y + heightPt > pdf.internal.pageSize.getHeight()) {
      this.addPage(pdf)
    }

    // Add the image and caption if it exists 
    pdf.addImage(imageB64, 'jpeg', this.cursor.x + this.imageLeftMargin, this.cursor.y, widthPt, heightPt)

    let captionHeight = 0
    if (caption) {
      pdf.setFontSize(this.footerTextSize)

      const lines = pdf.splitTextToSize(caption, widthPt)
      pdf.text(lines, this.cursor.x + this.imageLeftMargin, this.cursor.y + this.imageBottomMargin + heightPt)

      // Add caption height plus a margin to avoid line crashing
      captionHeight = lines.length * this.footerTextSize * pdf.getLineHeightFactor() + this.imageBottomMargin
    }

    // Reset cursor to the right of the image
    this.cursor = { ...this.cursor, x: this.imageLeftMargin + this.cursor.x + widthPt + this.imageRightMargin }

    return heightPt + captionHeight
  }

  /**
   * @function addPlacehodler
   * 
   * @param {jsPDF.Document} pdf
   * @param {String} type
   * 
   * Inserts a placeholder SVG for this type of entity
   * 
   * @returns Insert height of the image
   **/ 
  async addPlaceholder(pdf, type) {
    let svgUrl
    switch (type) {
    case 'actor':
      svgUrl = '/assets/' + actorImage
      break
    case 'archive':
      svgUrl = '/assets/' + archiveImage
      break
    case 'exhibition':
      svgUrl = '/assets/' + exhibitionImage
      break
    case 'library':
      svgUrl = '/assets/' + libraryImage
      break
    case 'object':
      svgUrl = '/assets/' + objectImage
      break
    }

    const svg = await fetch(svgUrl, { headers: { "Accept": "image/svg+xml" } })
    const image = await svg.text()

    // If image overflows the page, break the page
    const sizePt = this.imageWidth * 0.75

    if (this.cursor.y + sizePt > pdf.internal.pageSize.getHeight()) {
      this.addPage(pdf)
    }

    // Plot the SVG data onto a canvas via canvg
    // Adapted from https://parallax.github.io/jsPDF/docs/modules_svg.js.html#line78 but with dimensions
    const canvas = document.createElement('canvas')
    canvas.width = this.imageWidth
    canvas.height = this.imageWidth

    const context = canvas.getContext('2d')
    context.fillStyle = '#fff'
    context.fillRect(0, 0, this.imageWidth, this.imageWidth)

    const instance = await Canvg.fromString(context, image, { ignoreMouse: true, ignoreAnimation: true })
    const rendered = await instance.render({ ignoreMouse: true, ignoreAnimation: true })

    // Add the image
    pdf.addImage(canvas.toDataURL("image/jpeg", 1.0), 'jpeg', this.cursor.x + this.imageLeftMargin, this.cursor.y, sizePt, sizePt)
    this.cursor = { ...this.cursor, x: this.imageLeftMargin + this.cursor.x + sizePt + this.imageRightMargin }

    return sizePt 
  }

  addTombstone(pdf, item) {
    const { x: startX } = this.cursor

    const { id, description, imageDownload, imageUrl, filename_slug } = item

    const leftHeight = this.addLeftTombstone(pdf, item)
    const rightHeight = this.addRightTombstone(pdf, item)

    // Set the total height for the column
    const marginHeight = 1 * this.bodySize * pdf.getLineHeightFactor()
    const columnsHeight = Math.max(leftHeight, rightHeight) + marginHeight
    let totalHeight = columnsHeight

    // Move the cursor below the tombstone
    this.cursor.y += columnsHeight

    // Break if necessary and reset the x if so
    this.breakIfNeeded(pdf, this.bodySize)
    if (this.cursor.x !== startX) this.cursor.x = startX

    if (description) {
      // Break description lines by the container width before insert
      const width = pdf.internal.pageSize.getWidth() * .5
      const lines = pdf.splitTextToSize(description, width)

      this.addText(pdf, lines, this.bodySize, 'normal')

      totalHeight += lines.length * this.bodySize * pdf.getLineHeightFactor()      
    }

    // Add the image filename label
    let imageLabel = 'No image download available.'
    if (imageDownload) {
      const imageNumber = imageUrl.split('/').at(-1)
      const filename = `${filename_slug}_${imageNumber}.jpg`

      imageLabel = `Image: ${filename}`
    }

    this.addText(pdf, imageLabel, this.footerTextSize, 'normal')
    totalHeight += this.footerTextSize * pdf.getLineHeightFactor()

    // Add link under the tombstone or description
    const link = new URL(id, window.location.origin).href
    pdf.setFontSize(this.footerTextSize)
    pdf.textWithLink(`URL: ${link}`, this.cursor.x, this.cursor.y, { url: link })

    // Move the cursor and add to the total height
    this.cursor.y += this.bodySize * pdf.getLineHeightFactor()
    totalHeight += this.bodySize * pdf.getLineHeightFactor()      

    return totalHeight
  }

  addLeftTombstone(pdf, item) {
    // Insert the left tombstone column..
    const { 
            date,
            id,
            maker,
            title,
            cr_data,
            dimensions,
            materials,
            description,
            type
    } = item

    const { x: startX, y: startY } = this.cursor
    let totalHeight = 0

    // The tombstone width should be 4 units of a 12-column of the available content width 
    const innerWidth = ( pdf.internal.pageSize.getWidth() - this.leftMargin - this.rightMargin ) / 12 * 4

    // Maker in normal
    const makerLines = pdf.splitTextToSize(maker, innerWidth)
    this.addText(pdf, makerLines, this.bodySize, 'normal')
    totalHeight += makerLines.length * this.bodySize * pdf.getLineHeightFactor()

    // .. Title and date in bold 
    pdf.setFontSize(this.titleSize)
    pdf.setFont('Montserrat', 'bold')
    const titleLabel = `${title ?? ""}, ${date}`

    const titleLines = pdf.splitTextToSize(titleLabel, innerWidth)
    this.addText(pdf, titleLines, this.titleSize, 'bold')

    totalHeight += titleLines.length * this.titleSize * pdf.getLineHeightFactor()

    // .. materials and dimensions
    if (materials || dimensions) {
      const materialsDimensions = `${materials ?? ""}, ${dimensions ?? ""}`

      const lines = pdf.splitTextToSize(materialsDimensions, innerWidth)
      this.addText(pdf, lines, this.bodySize, 'normal')      

      totalHeight += lines.length * this.bodySize * pdf.getLineHeightFactor()
    }

    // .. CR number
    if (cr_data?.cr_number) {
      const label = `CR No. ${cr_data.cr_number}`
      this.addText(pdf, label, this.bodySize, 'normal')
      totalHeight += this.bodySize * pdf.getLineHeightFactor()
    }

    // Reset cursor to the start
    this.cursor.y = startY

    return totalHeight
  }

  addRightTombstone(pdf, item) {
    const { x: startX, y: startY } = this.cursor

    // The tombstone width should be 4 units of a 12-column of the available content width 
    const innerWidth = ( pdf.internal.pageSize.getWidth() - this.leftMargin - this.rightMargin ) / 12 * 4

    // Move the cursor to the right tombstone column
    this.cursor.x += innerWidth

    // Break the credit line and collection into lines and add to the document
    const { credit_line, collection } = item

    let creditLineLines = []
    let collectionLines = []

    if (credit_line) {
      creditLineLines = pdf.splitTextToSize(credit_line, innerWidth)    
    }

    if (collection) {
      collectionLines = pdf.splitTextToSize(collection, innerWidth)      
    }

    this.addText(pdf, collectionLines.concat(creditLineLines), this.bodySize, 'normal')

    // Reset the cursor
    this.cursor.x = startX
    this.cursor.x = startY

    const totalHeight = (creditLineLines.length + collectionLines.length) * this.bodySize * pdf.getLineHeightFactor()

    return totalHeight
  }

  addDataList(title, data, pdf) {
    const innerWidth = pdf.internal.pageSize.getWidth() - this.leftMargin - this.rightMargin
    const { x: startX, y: startY } = this.cursor
    let totalHeight = 0
    // Insert the section header
    this.addText(pdf, title, this.bodySize, 'bold')
    this.cursor.x += this.sectionInsetMargin
    totalHeight += this.bodySize * pdf.getLineHeightFactor()

    // Insert the datas
    switch (true) {
      case (typeof data === 'string'): {
        const lines = pdf.splitTextToSize(data, innerWidth)

        this.addText(pdf, lines, this.bodySize, 'normal')        
        totalHeight += lines.length * this.bodySize * pdf.getLineHeightFactor

        break        
      }
      case (Array.isArray(data) && data.length > 0): {
        for (const dataPoint of data) {
          if (typeof dataPoint === 'object') {
            const heading = SECTION_HEADERS[dataPoint.type]
            this.addText(pdf, heading, this.titleSize, 'bold')

            const lines = pdf.splitTextToSize(dataPoint.content, innerWidth)
            this.addText(pdf, lines, this.bodySize, 'normal')

            totalHeight += lines.length * this.bodySize * pdf.getLineHeightFactor()            
          } else if (typeof dataPoint === 'string') {
            const lines = pdf.splitTextToSize(dataPoint, innerWidth)
            this.addText(pdf, lines, this.bodySize, 'normal')

            totalHeight += lines.length * this.bodySize * pdf.getLineHeightFactor()            
          }
        }

        break        
      }
      case (typeof data === 'object'): {
        for (const [headingType, values] of Object.entries(data)) {
          const headerText = SECTION_HEADERS[headingType]

          if (!headerText) continue
          this.addText(pdf, headerText, this.bodySize, 'bold')

          // Add the datas -- walk values and add appropriately
          // Inscription-like values
          if (Array.isArray(values)) {
            for (const val of values) {
              const lines = pdf.splitTextToSize(val, innerWidth)
              this.addText(pdf, lines, this.bodySize, 'normal')  

              totalHeight += lines.length * this.bodySize * pdf.getLineHeightFactor          
            }
          } else if (typeof values === 'object' && values.titles?.length > 0) {
            for (const title of values.titles) {
              const titleString = typeof title === 'string' ? title : title.title
              const titleLines = pdf.splitTextToSize(titleString, innerWidth)
              const notesLines = typeof title === 'object' && title.notes ? pdf.splitTextToSize(title.notes, innerWidth) : []

              const lines = titleLines.concat(notesLines)
              this.addText(pdf, lines, this.bodySize, 'normal')                          

              totalHeight += lines.length * this.bodySize * pdf.getLineHeightFactor          
            }
          } 
        }
        break        
      }
    }

    this.cursor.x = startX
    return totalHeight
  }

  // Add text lines, inserting page breaks as needed
  addTextWithBreak(pdf, lines, size, style) {
    for (const line of lines) {
      this.addText(pdf, [line], size, style)
      this.breakIfNeeded(pdf, size)
    }
  }

  breakIfNeeded(pdf, size=this.bodySize) {
    const nextLineY = this.cursor.y + this.bodySize * pdf.getLineHeightFactor()
    if (nextLineY >= pdf.internal.pageSize.getHeight() - this.bottomMargin) {
      this.addPage(pdf)
    }
  }

  addConservation(pdf, conservation) {
    const {
      reporter,
      date,
      other_parties,
      report_notes,
      technical_description,
      condition_narrative
    } = conservation
    const innerWidth = pdf.internal.pageSize.getWidth() - this.leftMargin - this.rightMargin

    // Insert the report header 
    const reportLabel = `${date} Reported by: ${reporter.name} Conservator ${(other_parties ?? []).map(p => p.name).join(", ")}`
    this.addText(pdf, reportLabel, this.titleSize, 'normal')
    // TODO: Add date as a left side label

    if (report_notes) {
      // TOOD: Add on left side?
      this.addText(pdf, 'REPORT NOTES', this.titleSize, 'bold')

      const lines = pdf.splitTextToSize(report_notes, innerWidth)
      this.addTextWithBreak(pdf, lines, this.bodySize, 'normal')
    }

    if (technical_description) {
      // TOOD: Add on left side?
      this.addText(pdf, 'TECHNICAL DESCRIPTION', this.titleSize, 'bold')

      const lines = pdf.splitTextToSize(technical_description, innerWidth)
      this.addTextWithBreak(pdf, lines, this.bodySize, 'normal')
    }

    if (condition_narrative) {
      // TOOD: Add on left side?
      this.addText(pdf, 'CONDITION NARRATIVE', this.titleSize, 'bold')

      const lines = pdf.splitTextToSize(condition_narrative, innerWidth)
      this.addTextWithBreak(pdf, lines, this.bodySize, 'normal')
    }
  }

  addAbout(pdf, item, options) {
    const { x: startX, y: startY } = this.cursor
    const { data: dataOptions } = options
    const { cr_data } = item

    let totalHeight = 0

    if (dataOptions.provenance && cr_data?.provenance) {
      this.addDataList('Provenance', cr_data.provenance, pdf)
      this.breakIfNeeded(pdf)
    }

    if (dataOptions.titleDate && cr_data?.titles?.length > 0) {
      this.addDataList('Title and Date Details', cr_data.titles, pdf)
      this.breakIfNeeded(pdf)
    }

    if (dataOptions.remarks && cr_data?.remarks_1999?.length > 0) {
      // TODO: inscriptions
      this.addDataList('Remarks and Inscriptions', cr_data.remarks_1999, pdf)
      this.breakIfNeeded(pdf)
    }

    if (dataOptions.materials && cr_data?.materials?.length > 0) {
      this.addDataList('Materials', cr_data.materials, pdf)
      this.breakIfNeeded(pdf)
    }

    // TODO: Exhibition History
    // if (dataOptions.exhibitions && cr_data.exhibitions?.length > 0) {
    //   this.addDataList('Exhibition History', cr_data.exhibitions, pdf)
    // }

    if (dataOptions.references && cr_data?.references?.length > 0) {
      this.addDataList('Selected References', cr_data.references, pdf)
      this.breakIfNeeded(pdf)
    }

    if (dataOptions.conservation && cr_data?.conservation?.length > 0) {
      this.addText(pdf, 'Conservation', this.sectionHeadingSize, 'bold')
      const disclaimer = 'Information is from the most recently submitted report, please contact the current owner to verify updated details.'
      const innerWidth = pdf.internal.pageSize.getWidth() - this.leftMargin - this.rightMargin

      const lines = pdf.splitTextToSize(disclaimer, innerWidth)
      this.addText(pdf, lines, this.bodySize, 'normal')

      for (const report of cr_data.conservation) {
        this.addConservation(pdf, report)
        this.breakIfNeeded(pdf)        
      }
    }

    return totalHeight
  }

  async addItem(pdf, item, options) {
    const lineFactor = pdf.getLineHeightFactor()

    const { collection,
            date,
            id,
            imageUrl,
            imageCaption,
            maker,
            title,
            cr_data,
            credit_line,
            date_start,
            date_end,
            dimensions,
            materials,
            description,
            type
            } = item

    let imageHeight
    if (imageUrl) {
      imageHeight = await this.addImage(pdf, imageUrl, imageCaption)
    } else {
      imageHeight = await this.addPlaceholder(pdf, type)
    }

    const { y: imageStartY } = this.cursor

    const tombstoneHeight = this.addTombstone(pdf, item)

    const placedContentOffset = Math.max(tombstoneHeight + this.tombstoneBottomMargin, imageHeight + this.imageBottomMargin)

    // Reset the cursor to the left edge and below image content
    this.cursor.x = this.leftMargin

    // TODO: Should be able to do this for all items with placedContentOffset but wasn't working during launch
    // TombstoneHeight is likely incorrect, but the cursor moves *are* correct so this lets those stand
    if (imageHeight > tombstoneHeight) {
      this.cursor.y = imageStartY + imageHeight + this.imageBottomMargin      
    }

    this.addAbout(pdf, item, options)    

    this.addSeparatorLine(pdf)
  }

  async print(name, description, created, updated, data, options) {
    this.documentName = name

    const pdf = new jsPDF({
      orientation: 'p',
      unit: 'pt',
      format: 'letter',
      compress: true,
      putOnlyUsedFonts: true,
      hotfixes: ['px_scaling']
    })
  
    await this.setupFonts(pdf)

    this.addFooter(pdf)
    this.cursor = { x: this.leftMargin, y: this.topMargin }

    this.addListHeader(pdf, name, description, created, updated)

    pdf.setFontSize(this.bodySize)
    for (const [i, row] of data.entries()) {
      await this.addItem(pdf, row, options)

      // Break the page after every item except last, if extra data is enabled
      if ((options.data.about || options.data.conservation) && i < data.length - 1) {
        this.addPage(pdf)
      }
    }

    const blob = pdf.output('blob')

    return blob
  }
}

export default PDFPrinter